import { __async } from '../chunk-WE74IZDZ.mjs';
import { getAddress, UserRejectedRequestError, encodeFunctionData } from 'viem';
import { Connector, ConnectorNotFoundError } from 'wagmi';

var KasKeeperUserRejectedRequestError = class extends Error {
  constructor() {
    super(...arguments);
    this.name = "KasKeeperUserRejectedRequestError";
    this.message = "User rejected request";
  }
};
var KasKeeperConnector = class extends Connector {
  constructor(config = {}) {
    super({
      chains: config.chains,
      options: config.options || {}
    });
    this.id = "kasKeeper";
    this.name = "KasKeeper";
    this.ready = typeof window !== "undefined" && Boolean(window.Kaskeeper);
    this.onAccountsChanged = (accounts) => __async(this, null, function* () {
      if (accounts.length === 0) {
        this.emit("disconnect");
        return;
      }
      try {
        const provider = yield this.getProvider();
        if (!provider)
          return;
        yield KasKeeperConnector.ensureLayer2(provider);
        yield new Promise((resolve) => setTimeout(resolve, 1e3));
        const l2Address = yield this.getL2Address(provider);
        if (l2Address) {
          this.emit("change", {
            account: getAddress(l2Address)
          });
        }
      } catch (error) {
        console.warn("Failed to handle account change:", error);
        if (accounts[0]) {
          this.emit("change", {
            account: getAddress(accounts[0])
          });
        }
      }
    });
    this.onChainChanged = (_chainId) => {
      this.emit("change", { chain: { id: 42161, unsupported: false } });
    };
    this.onDisconnect = () => {
      this.emit("disconnect");
    };
    this.onNetworkChanged = (network) => __async(this, null, function* () {
      var _a, _b, _c, _d;
      if (network !== "kaspa_testnet") {
        try {
          const provider = yield this.getProvider();
          if (provider) {
            yield provider.switchNetwork("kaspa_testnet");
            yield KasKeeperConnector.ensureLayer2(provider);
            yield new Promise((resolve) => setTimeout(resolve, 1e3));
            const l2Address = yield this.getL2Address(provider);
            if (l2Address) {
              this.emit("change", {
                account: getAddress(l2Address),
                chain: { id: ((_b = (_a = this.chains) == null ? void 0 : _a[0]) == null ? void 0 : _b.id) || 42161, unsupported: false }
              });
              return;
            }
          }
        } catch (error) {
          console.warn("Failed to switch to testnet:", error);
        }
      }
      this.emit("change", { chain: { id: ((_d = (_c = this.chains) == null ? void 0 : _c[0]) == null ? void 0 : _d.id) || 42161, unsupported: false } });
    });
    this.onLayerChanged = (layer) => __async(this, null, function* () {
      var _a, _b, _c, _d;
      if (layer !== "L2") {
        try {
          const provider = yield this.getProvider();
          if (provider) {
            yield provider.switchLayer("L2");
            yield new Promise((resolve) => setTimeout(resolve, 800));
            const l2Address = yield this.getL2Address(provider);
            if (l2Address) {
              this.emit("change", {
                account: getAddress(l2Address),
                chain: { id: ((_b = (_a = this.chains) == null ? void 0 : _a[0]) == null ? void 0 : _b.id) || 42161, unsupported: false }
              });
              return;
            }
          }
        } catch (error) {
          console.warn("Failed to switch back to Layer 2:", error);
        }
      }
      this.emit("change", { chain: { id: ((_d = (_c = this.chains) == null ? void 0 : _c[0]) == null ? void 0 : _d.id) || 42161, unsupported: false } });
    });
  }
  static convertBigIntToHex(value) {
    if (typeof value === "bigint") {
      return `0x${value.toString(16)}`;
    }
    if (Array.isArray(value)) {
      return value.map((item) => KasKeeperConnector.convertBigIntToHex(item));
    }
    if (typeof value === "object" && value !== null) {
      const converted = {};
      for (const [key, val] of Object.entries(value)) {
        converted[key] = KasKeeperConnector.convertBigIntToHex(val);
      }
      return converted;
    }
    return value;
  }
  connect() {
    return __async(this, arguments, function* ({ chainId } = {}) {
      var _a, _b;
      try {
        const provider = yield this.getProvider();
        if (!provider)
          throw new ConnectorNotFoundError();
        if (provider.on) {
          provider.on("accountsChanged", this.onAccountsChanged);
          provider.on("networkChanged", this.onNetworkChanged);
          provider.on("layerChanged", this.onLayerChanged);
        }
        this.emit("message", { type: "connecting" });
        yield provider.requestAccounts();
        yield KasKeeperConnector.ensureLayer2(provider);
        yield new Promise((resolve) => setTimeout(resolve, 1200));
        const l2Address = yield this.getL2Address(provider);
        if (!l2Address) {
          throw new Error("Failed to get Layer 2 Kasplex address.");
        }
        const accounts = yield provider.getAccounts();
        const account = getAddress(accounts[0]);
        const id = chainId || ((_b = (_a = this.chains) == null ? void 0 : _a[0]) == null ? void 0 : _b.id) || 167012;
        const unsupported = this.isChainUnsupported(id);
        return { account, chain: { id, unsupported }, provider };
      } catch (error) {
        if (KasKeeperConnector.isUserRejectedRequestError(error)) {
          throw new UserRejectedRequestError(error);
        }
        throw error;
      }
    });
  }
  disconnect() {
    return __async(this, null, function* () {
      const provider = yield this.getProvider();
      if (!provider)
        return;
      try {
        yield provider.disconnect();
      } catch (error) {
      }
      if (provider.removeListener) {
        provider.removeListener("accountsChanged", this.onAccountsChanged);
        provider.removeListener("networkChanged", this.onNetworkChanged);
        provider.removeListener("layerChanged", this.onLayerChanged);
      }
      this.emit("disconnect");
    });
  }
  getAccount() {
    return __async(this, null, function* () {
      const provider = yield this.getProvider();
      if (!provider)
        throw new ConnectorNotFoundError();
      yield KasKeeperConnector.requestAccounts(provider);
      yield KasKeeperConnector.ensureLayer2(provider);
      yield new Promise((resolve) => setTimeout(resolve, 1e3));
      const l2Address = yield this.getL2Address(provider);
      if (!l2Address) {
        throw new Error("Failed to get Layer 2 Kasplex address");
      }
      return getAddress(l2Address);
    });
  }
  getChainId() {
    return __async(this, null, function* () {
      var _a, _b;
      return ((_b = (_a = this.chains) == null ? void 0 : _a[0]) == null ? void 0 : _b.id) || 42161;
    });
  }
  getProvider() {
    return __async(this, null, function* () {
      if (typeof window !== "undefined" && window.Kaskeeper) {
        this.provider = window.Kaskeeper;
        return this.provider;
      }
      return void 0;
    });
  }
  getWalletClient() {
    return __async(this, arguments, function* ({ chainId } = {}) {
      const provider = yield this.getProvider();
      if (!provider)
        throw new ConnectorNotFoundError();
      yield KasKeeperConnector.ensureLayer2(provider);
      const account = yield this.getAccount();
      const chain = this.chains.find((c) => {
        var _a;
        return c.id === (chainId || ((_a = this.chains[0]) == null ? void 0 : _a.id));
      });
      if (!chain)
        throw new Error("Chain not found");
      return {
        account: {
          address: account,
          type: "json-rpc"
        },
        chain,
        transport: {
          type: "custom",
          request: (_0) => __async(this, [_0], function* ({ method, params }) {
            if (method === "eth_requestAccounts") {
              yield KasKeeperConnector.requestAccounts(provider);
              yield KasKeeperConnector.ensureLayer2(provider);
              yield new Promise((resolve) => setTimeout(resolve, 500));
              const l2Address = yield this.getL2Address(provider);
              return l2Address ? [l2Address] : [];
            }
            if (method === "eth_accounts") {
              yield KasKeeperConnector.ensureLayer2(provider);
              yield new Promise((resolve) => setTimeout(resolve, 500));
              const l2Address = yield this.getL2Address(provider);
              return l2Address ? [l2Address] : [];
            }
            if (method === "eth_chainId") {
              return `0x${chain.id.toString(16)}`;
            }
            if (method === "personal_sign" && Array.isArray(params)) {
              return provider.signMessage(params[0]);
            }
            if (method === "eth_getBalance") {
              try {
                const balance = yield provider.getBalance();
                return `0x${(balance.total || 0).toString(16)}`;
              } catch (error) {
                return "0x0";
              }
            }
            if (method === "net_version") {
              return chain.id.toString();
            }
            if (method === "eth_sendTransaction") {
              if (!Array.isArray(params) || !params[0]) {
                throw new Error("Invalid transaction parameters");
              }
              const txParams = params[0];
              if (!txParams.to) {
                throw new Error("Transaction must have a recipient address");
              }
              try {
                yield KasKeeperConnector.ensureLayer2(provider);
                yield new Promise((resolve) => setTimeout(resolve, 1e3));
                const convertedParams = KasKeeperConnector.convertBigIntToHex(txParams);
                if (provider.sendL2Specailtx && convertedParams.data && convertedParams.data !== "0x") {
                  const amount = convertedParams.value ? parseInt(convertedParams.value, 16) : 0;
                  return yield provider.sendL2Specailtx(convertedParams.to, amount, convertedParams.data);
                }
                if (provider.sendL2Kaspa) {
                  const amount = convertedParams.value ? parseInt(convertedParams.value, 16) : 0;
                  return yield provider.sendL2Kaspa(convertedParams.to, amount);
                }
                throw new Error("KasKeeper transaction methods not available");
              } catch (error) {
                throw new Error(`KasKeeper transaction failed: ${error}.`);
              }
            }
            if (method === "eth_estimateGas") {
              return "0x5208";
            }
            if (method === "eth_gasPrice") {
              return "0x9184e72a000";
            }
            if (method === "eth_getTransactionCount") {
              return "0x0";
            }
            throw new Error(`Method ${method} not supported by KasKeeper`);
          })
        },
        // Add the writeContract method
        writeContract: (args) => __async(this, null, function* () {
          const { address, abi, functionName, args: contractArgs, value, gas } = args;
          if (!address)
            throw new Error("Contract address is required");
          if (!abi)
            throw new Error("Contract ABI is required");
          if (!functionName)
            throw new Error("Function name is required");
          const data = encodeFunctionData({
            abi,
            functionName,
            args: contractArgs || []
          });
          if (!data || data === "0x") {
            throw new Error("Failed to encode function data");
          }
          try {
            yield KasKeeperConnector.ensureLayer2(provider);
            yield new Promise((resolve) => setTimeout(resolve, 1e3));
          } catch (error) {
            throw new Error(`Failed to ensure correct network/layer: ${error}`);
          }
          if (!address || !data) {
            throw new Error("Invalid transaction parameters");
          }
          const txRaw = {
            from: account,
            to: address,
            gas,
            gasPrice: "2001000000000",
            data,
            value
          };
          const tx = KasKeeperConnector.convertBigIntToHex(txRaw);
          try {
            if (!provider.sendL2ERC20)
              throw new Error("KasKeeper L2PayloadTransfer not available");
            const result = yield provider.sendL2ERC20(tx, { priorityFee: 21e-6 });
            return result;
          } catch (error) {
            throw new Error(`KasKeeper writeContract failed: ${error}. Contract call to ${functionName} on ${address}.`);
          }
        }),
        sendTransaction: (args) => __async(this, null, function* () {
          try {
            const converted = KasKeeperConnector.convertBigIntToHex(args);
            if (provider.sendL2ERC20) {
              return yield provider.sendL2ERC20(
                {
                  to: converted.to,
                  value: converted.value,
                  data: converted.data || "0x",
                  gas: converted.gas,
                  gasPrice: converted.gasPrice || "2001000000000"
                },
                { priorityFee: 21e-6 }
              );
            }
            if (!provider.sendL2Specailtx)
              throw new Error("KasKeeper transaction methods not available");
            const toAddress = converted.to;
            const kasAmount = converted.value ? parseInt(converted.value, 16) : 0;
            const data = converted.data || "0x";
            const result = yield provider.sendL2Specailtx(toAddress, kasAmount, data, 21e-6);
            return result;
          } catch (error) {
            throw new Error(`KasKeeper sendTransaction failed: ${error}.`);
          }
        }),
        signMessage: (_0) => __async(this, [_0], function* ({ message }) {
          return provider.signMessage(message);
        }),
        signTransaction: (transaction) => __async(this, null, function* () {
          const convertedTransaction = KasKeeperConnector.convertBigIntToHex(transaction);
          throw new Error(
            `KasKeeper does not support transaction signing for transaction to ${convertedTransaction.to}. Please use KasKeeper's native transaction interface.`
          );
        }),
        type: "kasKeeper"
      };
    });
  }
  static requestAccounts(provider) {
    return __async(this, null, function* () {
      return provider.requestAccounts();
    });
  }
  isAuthorized() {
    return __async(this, null, function* () {
      try {
        const provider = yield this.getProvider();
        if (!provider)
          return false;
        yield KasKeeperConnector.ensureLayer2(provider);
        yield new Promise((resolve) => setTimeout(resolve, 500));
        const l2Address = yield this.getL2Address(provider);
        return Boolean(l2Address);
      } catch (e) {
        return false;
      }
    });
  }
  switchChain(chainId) {
    return __async(this, null, function* () {
      var _a;
      const chain = (_a = this.chains) == null ? void 0 : _a.find((c) => c.id === chainId);
      if (chain) {
        return chain;
      }
      throw new Error("Chain switching not supported by KasKeeper - use switchNetwork method instead");
    });
  }
  // Helper method to get Layer 2 Kasplex address specifically
  // eslint-disable-next-line class-methods-use-this
  getL2Address(provider) {
    return __async(this, null, function* () {
      try {
        const currentLayer = yield provider.getLayer();
        if (currentLayer !== "L2") {
          throw new Error(`Must be on Layer 2, currently on: ${currentLayer}`);
        }
        const accounts = yield provider.getAccounts();
        if (!accounts || accounts.length === 0) {
          throw new Error("No Layer 2 accounts found");
        }
        const l2Address = accounts[0];
        if (!l2Address.startsWith("0x")) {
          throw new Error(`Invalid Layer 2 address format: ${l2Address}. Expected 0x prefix for Kasplex addresses.`);
        }
        return l2Address;
      } catch (error) {
        console.error("Failed to get L2 address:", error);
        return null;
      }
    });
  }
  // Helper method to ensure we're on Layer 2
  static ensureLayer2(provider, forceSwitch = false) {
    return __async(this, null, function* () {
      try {
        const currentLayer = yield provider.getLayer();
        if (currentLayer !== "L2" || forceSwitch) {
          yield provider.switchLayer("L2");
          yield new Promise((resolve) => setTimeout(resolve, 800));
          const finalLayer = yield provider.getLayer();
          if (finalLayer !== "L2") {
            throw new Error(`Failed to switch to Layer 2. Current layer: ${finalLayer}`);
          }
        }
      } catch (error) {
        throw new Error(`Failed to ensure Layer 2: ${error}`);
      }
    });
  }
  static isUserRejectedRequestError(error) {
    return error instanceof Error && error.message.includes("rejected");
  }
};

export { KasKeeperConnector, KasKeeperUserRejectedRequestError };
